import React, { useEffect, useState } from "react";
import { CheckCircle, XCircle, Eye, GitBranch } from "lucide-react";
import DynamicTable from "../../../../components/learning-development/common/DynamicTable";
import ModalWrapper from "../../../../components/learning-development/common/ModalWrapper";

import api from "../../../../Axios";
import { toast } from "react-toastify";
import useAuthUser from "../../../../hooks/useAuthUser";
import CommonFilter from "../../../../components/common/CommonFilter";
import { Button, TextField, Tooltip } from "@mui/material";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import useModal from "../../../../hooks/useModal";
import useLoader from "../../../../hooks/useLoader";
import useConfirm from "../../../../hooks/useConfirm";
import ExpandableText from "../../../../components/common/ExpandableText";
import { capitalizeFirstLetter, defaultDateFormatter, formatDateTime } from "../../../../Helper/functionHelper";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UserInfoCell from "../../../../components/common/UserInfoCell";

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const UserProgramRequestLIst = () => {

  const { loggedInUser } = useAuthUser();
  const userRole = Number(loggedInUser?.role);

  const { showModal, hideModal } = useModal();

  const { hideLoader, showLoader } = useLoader();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [modalType, setModalType] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [requestFlow, setRequestFlow] = useState([]);
  const [documentPath, setDocumentPath] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    start_date: null,
    end_date: null
  });

  const [filterConfig, setFilterConfig] = useState([
    {
      name: "search",
      type: "text",
      label: "Program title",
      grid: 3,
    },
    {
      name: "status",
      type: "select",
      label: "Status",
      grid: 3,
      options: [
        { label: "Pending", value: "draft" },
        { label: "Approved", value: "approved" },
        { label: "Rejected", value: "rejected" },
      ]
    },
    {
      name: "start_date",
      type: "date",
      label: "From Date",
      grid: 3,
    },
    {
      name: "end_date",
      type: "date",
      label: "To Date",
      grid: 3,
    },
  ]);

  const fetchRequests = async (page = currentPage, customFilters = filters) => {
    try {
      showLoader();

      const res = await api.post("/lnd/admin/elr/get-all", {
        page,
        limit: itemsPerPage,
        searchData: {
          ...customFilters,
          search: customFilters.search?.trim() || ""
        }
      });

      const data = res.data?.data?.data || [];
      const pagination = res.data?.data?.pagination;

      if (data.length === 0) {
        setRequests([]);
        setTotalPages(1);
        setTotalItems(0);
      } else {
        setRequests(data);
        setTotalPages(pagination.totalPages);
        setTotalItems(pagination.total);
      }
      setDocumentPath(res?.data?.data?.documentPath || "");
    } catch (err) {
      setRequests([]);
      setTotalPages(1);
      setTotalItems(0);
      toast.error("API failed");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchRequests(currentPage, filters);
  }, [currentPage, search]);

  const handleExport = async (filters) => {
    try {
      const res = await api.post(
        "/lnd/export-user-program-request",
        { searchData: filters },
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "user_program_request.xlsx";
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
    }
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedRequest(null);
    setRejectReason("");
  };

  const openModal = async (type, row) => {
    try {
      const res = await api.get(`/approval/get-approval-request-flow?request_id=${row['approval.id']}`);
      console.log("Flow response:", res?.data?.data?.workflow?.data);
      setRequestFlow(res?.data?.data?.workflow?.data || []);
    } catch (error) {
      console.log("Error fetching request flow:", error);
      toast.error("Failed to load approval flow");
    }
    setSelectedRequest(row);
    setModalType(type);
  };

  const { confirm } = useConfirm();
  const approveRequest = async (type, row) => {
    setSelectedRequest(row);
    try {
      const isConfirmed = await confirm({
        title: "Confirm Request Approval",
        content: `Are you sure you want to approve this request?`,
        confirmText: "Yes, Approve",
        cancelText: "Cancel"
      });
      if (!isConfirmed) return;

      await api.post(`/approval/action-approval`, {
        "requestId": row["approval.id"],
        "action": "APPROVE",
        "remarks": userRole === 1 ? "Approved by ADMIN" : userRole === 4 ? "Approved by HR MANAGER" : userRole === 5 ? "Approved by HR MANAGER" : "Approved by Third Party"
      });
      toast.success("Request approved successfully");
      fetchRequests(currentPage, filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete module");
    }
  };

  const handleApproveReject = (requestId, action) => {
    console.log(action, "action");
    const title = `${capitalizeFirstLetter(action)} ODD`;

    const ApproveRejectForm = () => {
      const [reason, setReason] = useState("");
      const [error, setError] = useState("");
      const [loading, setLoading] = useState(false);

      const handleSubmit = async () => {
        if (action === "reject" && reason.trim() === "") {
          setError("Reason is required for rejection.");
          return;
        }

        setLoading(true);

        try {
          await api.post(`/approval/action-approval`, {
            requestId,
            "action": action === "reject" ? "REJECT" : "APPROVE",
            "remarks": reason
          });
          toast.success(`Request ${action}`);
          fetchRequests(currentPage, filters);
        } catch (error) {
          toast.error(
            error?.response?.data?.message || "An unexpected error occurred."
          );
        } finally {
          setLoading(false);
          hideModal();
        }
      };

      return (
        <div className="py-2">
          <TextField
            fullWidth
            multiline
            minRows={3}
            variant="standard"
            label="Reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError("");
            }}
            error={!!error}
            helperText={error}
          />
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="dark"
              className="me-2"
              onClick={() => hideModal()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                handleSubmit();
              }}
              disabled={loading}
            >
              {capitalizeFirstLetter(action)}
            </Button>
          </div>
        </div>
      );
    };

    showModal(title, <ApproveRejectForm />);
  };

  const handleCertficateOpen = (key, title, certificate_img = "") => {
    let body;

    if (key === "certificate") {
      body = (
        <div className="text-center">
          {certificate_img ? (
            <img
              src={BASE_URL + documentPath + certificate_img}
              alt="Certificate"
              style={{
                maxWidth: "100%",
                maxHeight: "500px",
                borderRadius: "8px"
              }}
            />
          ) : (
            <p>No certificate available</p>
          )}
        </div>
      );
    }

    showModal(title, body);
  };


  const openViewModal = async (selectedRequest) => {
    let title = "View Program Details";

    const openLink = (url) => {
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
    };

    const body = (
      <div className="container-fluid">
        <div className="row g-3">

          {/* Title */}
          <div className="col-4 fw-semibold">Title</div>
          <div
            className="col-8 text-muted"
            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
          >
            {selectedRequest.learning_title || "N/A"}
          </div>

          {/* Provider */}
          <div className="col-4 fw-semibold">Provider</div>
          <div className="col-8">
            {selectedRequest.learning_provider || "N/A"}
          </div>

          {/* URL */}
          <div className="col-4 fw-semibold">URL</div>
          <div className="col-8">
            {selectedRequest.learning_url ? (
              <Tooltip title={selectedRequest.learning_url}>
                <span
                  onClick={() => openLink(selectedRequest.learning_url)}
                  style={{
                    cursor: "pointer",
                    color: "#0d6efd",
                    textDecoration: "underline",
                    fontWeight: 500,
                  }}
                >
                  {selectedRequest.learning_url.length > 50
                    ? `${selectedRequest.learning_url.slice(0, 20)}...Visit Link`
                    : "Visit Link"}
                </span>
              </Tooltip>
            ) : (
              "N/A"
            )}
          </div>

          {/* Cost */}
          <div className="col-4 fw-semibold">Cost</div>
          <div className="col-8">
            {selectedRequest.estimated_cost
              ? `₹ ${selectedRequest.estimated_cost}`
              : "N/A"}
          </div>

          {/* Description */}
          <div className="col-4 fw-semibold">Description</div>
          <div
            className="col-8 text-muted"
            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
          >
            {selectedRequest.description || "N/A"}
          </div>

          {/* Status */}
          <div className="col-4 fw-semibold">Status</div>
          <div className="col-8">
            {selectedRequest?.approval?.status || "N/A"}
          </div>

        </div>
      </div>
    );

    showModal(title, body);
  };

  // Table columns
  const columns = [
    {
      label: "Title",
      render: (row) => (
        <ExpandableText text={row.learning_title} label="Request Title" />
      )
    },
    {
      label: "Provider Name",
      render: (row) => (
        row.learning_provider ? row.learning_provider : "--"
      )
    },
    {
      label: "Employee Name (Code)",
      render: (row) => (
        <div>
          {UserInfoCell({ name: `${row["user._FirstName"]} ${row["user._MiddleName"]} ${row["user._LastName"]}`, employeeId: row["user._UserEmployeeID"], token: row["user._UserId"] })}
        </div>
      )
    },
    {
      label: "Cost(₹)",
      render: (row) => row.estimated_cost ? `${row.estimated_cost}` : "--"
    },
    {
      label: "Rejection Reason",
      render: (row) => (
        <ExpandableText text={row["approval.reject_reason"] || "--"} label="Rejection Reason" />
      )
    },
    {
      label: "View Certificate",
      render: (row) => {
        if (row["approval.status"] === "APPROVED" && row?.certificate_img) {
          return (
            <div className="d-flex">

              <Tooltip title="View" placement="top" arrow>
                <RemoveRedEyeIcon
                  className="cursor-pointer"
                  sx={{ color: "black", marginLeft: "8px" }}
                  onClick={() => handleCertficateOpen("certificate", "Certificate", row?.certificate_img)}
                />
              </Tooltip>
            </div>
          );
        }
        return "--";
      }
    },
    {
      label: "Final Approval Status",
      render: (row) => (
        <span className={`text-${row["approval.status"] === "APPROVED" ? "success" : row["approval.status"] === "REJECTED" ? "danger" : "warning"}`}>
          {row["approval.status"]}
        </span>
      )
    },
    {
      label: "Requested Date",
      render: (row) => (
        defaultDateFormatter(row.created_at)
      )
    },
    {
      label: "Actions",
      render: (row) => {
        const roleIds = JSON.parse(row?.current_level_role_id || "[]");

        const isAllowed = roleIds.includes(String(userRole));

        const isDisabled =
          row["approval.status"] !== "PENDING" || !isAllowed;

        return (
          <div className="d-flex gap-2">
            <div
              className="cursor-pointer"
              onClick={() => openViewModal(row)}
            >
              <Eye size={16} />
            </div>

            <div
              className="cursor-pointer"
              onClick={() => openModal("track", row)}
            >
              <GitBranch size={16} />
            </div>

            {!isDisabled && (
              <div
                className="cursor-pointer"
                onClick={() => handleApproveReject(row["approval.id"], "approve")}
              >
                <CheckCircle size={16} />
              </div>
            )}

            {!isDisabled && (
              <div
                className="cursor-pointer"
                onClick={() => handleApproveReject(row["approval.id"], "reject")}
              >
                <XCircle size={16} />
              </div>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <div className="container-fluid py-4">

      <div className="row g-2 align-items-end mb-4">

        {/* FILTER SECTION */}
        <div className="col-12 col-lg">
          <CommonFilter
            config={filterConfig}
            defaultValues={{
              search: "",
              status: "",
              start_date: null,
              end_date: null
            }}
            onSubmit={(data) => {
              setFilters(data);
              setCurrentPage(1);
              fetchRequests(1, data);
            }}
            onExport={handleExport}
          />
        </div>

      </div>

      {/* <TextField
        label="Search"
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3"
      /> */}

      <DynamicTable
        columns={columns}
        data={requests}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={(page) => setCurrentPage(page)}
        rowKey="id"
        loading={loading}
      />

      {/* TRACK FLOW MODAL */}
      {modalType === "track" && (() => {

        const formatFlow = (data) => {
          return data.map((item, index) => {
            let label = "";
            let role_name = "";

            if (index === 0) {
              label = `Requested by ${[item?.user?._FirstName, item?.user?._MiddleName, item?.user?._LastName]
                .filter(Boolean)
                .join(' ')
                }`;
              role_name = "Employee";
            } else {
              role_name = item?.role?.role_title || "";
              const actionText =
                item.action === "APPROVE"
                  ? "Approved"
                  : item.action === "REJECT"
                    ? "Rejected"
                    : "";

              label = `${actionText} by ${[item?.user?._FirstName, item?.user?._MiddleName, item?.user?._LastName]
                .filter(Boolean)
                .join(' ')
                }`;
            }

            return {
              label,
              role_name,
              date: item.created_at,
              status:
                item.action === "REJECT"
                  ? "rejected"
                  : "completed",
            };
          });
        };

        const steps = formatFlow(requestFlow || []);

        const rejectedIndex = steps.findIndex(
          (s) => s.status === "rejected"
        );

        const totalSteps = steps.length;

        const progressWidth =
          rejectedIndex !== -1
            ? ((rejectedIndex + 1) / totalSteps) * 100
            : (totalSteps / totalSteps) * 100;

        const progressColor =
          rejectedIndex !== -1 ? "#dc3545" : "#198754";

        return (
          <ModalWrapper onClose={closeModal}>

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">

              <h5 className="fw-bold mb-0">Approval Flow</h5>

              <button
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "20px",
                  cursor: "pointer",
                  lineHeight: "1"
                }}
              >
                ✕
              </button>

            </div>

            {/* FLOW */}
            <div>
              <div className="d-flex justify-content-between align-items-center position-relative">

                {/* Base Line */}
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "0",
                    right: "0",
                    height: "2px",
                    background: "#dee2e6",
                    zIndex: 0,
                  }}
                />

                {/* Progress Line */}
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "0",
                    width: `${progressWidth}%`,
                    height: "2px",
                    background: progressColor,
                    zIndex: 1,
                  }}
                />

                {/* Steps */}
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="d-flex flex-column align-items-center flex-fill position-relative"
                  >
                    {/* Circle */}
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        backgroundColor:
                          step.status === "completed"
                            ? "#198754"
                            : step.status === "rejected"
                              ? "#dc3545"
                              : "#adb5bd",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {step.status === "completed" && "✓"}
                      {step.status === "rejected" && "✕"}
                    </div>

                    <div
                      className={`mt-2 text-center small fw-semibold ${step.status === "rejected" ? "text-danger" : ""
                        }`}
                    >
                      {step.label} ({step.role_name})
                    </div>

                    {/* Date */}
                    <div className="text-muted small text-center">
                      {step.date
                        ? formatDateTime(step.date)
                        : ""}
                    </div>

                  </div>
                ))}

              </div>
            </div>

          </ModalWrapper>
        );

      })()}
    </div>
  );
};

export default UserProgramRequestLIst;